import type { Signal } from '@angular/core';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { CasesGamePhases, ICaseSpinItem, IGame, ModalModel } from '@dev-fast/types';
import { ModalNames } from '@dev-fast/types';
import { Actions, dispatch, Store } from '@ngxs/store';
import { BreakpointsService } from 'breakpoints-service';
import { DateTime } from 'luxon';
import { map } from 'rxjs';

import { ModalsService } from '@app/core/modal-service';
import type { IQuest } from '@app/core/quest';
import { GamesState } from '@app/core/state/games-store';
import { ModalsState, OpenModal } from '@app/core/state/modals';
import { actionLoadStatus } from '@app/shared/utils';

import { AprilMadnessEventModalsService } from '../services';
import {
  AprilMadnessEventSelectors,
  CollectLevelReward,
  GetAprilMadnessEventState,
  GetMotivator,
  GetPossibleItems,
  Replay,
  TakeReward,
} from '../state/';
import type { ICurrentLevelQuestLevel, IEventLevel, ILastItem, IPossibleItem, ITopGamer } from '../types/frontend';
import { AprilMadnessQuestModalComponent } from '../ui/modals/april-madness-quest-modal/april-madness-quest-modal.component';

const APRIL_MADNESS_QUEST_MODAL_NAME = ModalNames.ICE_BREAKERS_QUEST;

@Injectable({
  providedIn: 'root',
})
export class AprilMadnessEventService {
  readonly #store = inject(Store);
  readonly #actions = inject(Actions);
  readonly #aprilMadnessEventModalsService = inject(AprilMadnessEventModalsService);
  readonly #breakpointService = inject(BreakpointsService);
  readonly #commonModalsService = inject(ModalsService);
  readonly REQUIRED_HEADSHOTS = 5;

  readonly #modals: ModalModel[] = [
    {
      name: APRIL_MADNESS_QUEST_MODAL_NAME,
      component: AprilMadnessQuestModalComponent,
      config: {
        autoFocus: 'dialog',
        mobileViewAllowed: true,
        mobileMaxSize: 'm',
      },
    },
  ];

  /* REQUEST STATUSES */
  isEventStateLoading = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.isEventStateLoading);
  getPossibleItemsLoadStatus = toSignal(actionLoadStatus(this.#actions, GetPossibleItems));
  takeRewardLoadStatus = toSignal(actionLoadStatus(this.#actions, TakeReward));
  getMotivatorLoadStatus = toSignal(actionLoadStatus(this.#actions, GetMotivator));

  /* PROPERTIES */
  endAt: Signal<string> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.endAt);
  currentStep: Signal<number> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.currentStep);
  currentExp: Signal<number> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.currentExp);
  collected: Signal<number> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.collected);
  userTakenItems: Signal<ILastItem[]> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.userTakenItems);
  eventLevels: Signal<IEventLevel[]> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.eventLevels);
  possibleItems: Signal<IPossibleItem[]> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.possibleItems);
  topOfGamers: Signal<ITopGamer[] | undefined> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.topOfGamers);
  prizeSpinTracks: Signal<ICaseSpinItem[][]> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.prizeSpinTracks);
  spinState: Signal<CasesGamePhases | undefined> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.spinState);
  nextLevelRequiredExp: Signal<number> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.nextLevelRequiredExp);
  currentLevelProgressPercent: Signal<number> = this.#store.selectSignal(AprilMadnessEventSelectors.getSlices.currentLevelProgressPercent);
  currentLevelQuestLevel: Signal<ICurrentLevelQuestLevel | null> = this.#store.selectSignal(
    AprilMadnessEventSelectors.getSlices.currentLevelQuestLevel,
  );

  readonly headshots = computed(() => {
    const currentExp = this.currentExp();
    const nextLevelRequiredExp = this.nextLevelRequiredExp();

    if (!currentExp || currentExp <= 0 || !nextLevelRequiredExp || nextLevelRequiredExp <= 0) {
      return 0;
    }

    const stepSize = nextLevelRequiredExp / this.REQUIRED_HEADSHOTS;
    const step = Math.floor(currentExp / stepSize);

    return Math.max(1, Math.min(this.REQUIRED_HEADSHOTS, step));
  });

  isSomeModalOpened = this.#store.selectSignal<boolean>(ModalsState.isSomeModalOpened);

  readonly availableGames = this.#store.selectSignal<IGame[]>(GamesState.availableGames);

  untilEventTime$ = this.#store
    .select(AprilMadnessEventSelectors.getSlices.endAt)
    .pipe(map((endAt) => DateTime.fromISO(endAt).diff(DateTime.now()).milliseconds));

  /* RIGHT SIDEBAR */
  readonly isMobile = toSignal(this.#breakpointService.isBreakpointActive$(['tb', 'lp', 'dk', 'wd'], true), { initialValue: false });
  readonly aprilMadnessEventDrawIsOpened = signal(false);
  readonly aprilMadnessEventButton = computed(() => ({
    icon: 'april-madness-event',
    key: 'april-madness-event',
    label: 'april-madness-event',
    progressIcon: 'april-madness-event-progress',
    progress: this.currentLevelProgressPercent(),
    callback: (): void => {
      if (!this.isMobile()) {
        return;
      }
      this.#aprilMadnessEventModalsService.openLevelProgressModal();
    },
    hoverCallback: (isHover: boolean): void => {
      if (this.isMobile()) {
        return;
      }
      this.handleAprilMadnessEventSidebarIconHover();
    },
  }));

  readonly endAtDateTime = computed(() => {
    const endAtValue = this.endAt();
    return endAtValue ? DateTime.fromISO(endAtValue) : null;
  });

  readonly isEventActive = computed<boolean>(() => {
    const endAtDateTime = this.endAtDateTime();
    return endAtDateTime ? endAtDateTime.diff(DateTime.now()).milliseconds > 0 : false;
  });

  getEventState = dispatch(GetAprilMadnessEventState);
  takeReward = dispatch(TakeReward);
  getMotivator = dispatch(GetMotivator);
  replay = dispatch(Replay);
  getPossibleItems = dispatch(GetPossibleItems);
  collectLevelReward = dispatch(CollectLevelReward);
  openModal = dispatch(OpenModal);

  handleAprilMadnessEventSidebarIconHover = (): void => {
    this.aprilMadnessEventDrawIsOpened.set(true);
  };

  closeAprilMadnessEventSidebarPanel = (): void => {
    this.aprilMadnessEventDrawIsOpened.set(false);
  };

  registerModals(): void {
    this.#commonModalsService.registerModals(this.#modals);
  }

  openQuestModal(quest: IQuest): void {
    this.openModal(APRIL_MADNESS_QUEST_MODAL_NAME, quest);
  }
}
